import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { EntityDto, UserDtoPOST } from '@dto';
import { Account, NewUserForm, TypedFormBuilder } from '@model';
import { EntityService, GlobalAlertService, LocaleService, TimezoneService, UserService } from '@service';
import { ApplicationService } from 'src/app/core/service/application.service';

@Component({
  selector: 'login-new-user-form',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  styleUrls: ['./new-user-form.component.scss'],
  templateUrl: './new-user-form.component.html',
})
export class NewUserFormComponent implements OnInit {
  generatedPassword = '';
  accountsWithEntities: Account[];
  newUserForm: FormGroup;

  constructor(
    public applicationService: ApplicationService,
    private desEntityService: EntityService,
    private globalAlertService: GlobalAlertService,
    public localeService: LocaleService,
    public timezoneService: TimezoneService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
  ) {
    const resolvedData = this.route.snapshot.data['updates'];
    this.accountsWithEntities = resolvedData[0];
  }

  ngOnInit(): void {
    this.newUserForm = this.fb.group<TypedFormBuilder<NewUserForm>>({
      email: new FormControl('', [Validators.required, Validators.email]),
      firstName: new FormControl('', [Validators.required]),
      homeApp: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      locale: new FormControl('', [Validators.required]),
      phone: new FormControl('', [Validators.required]),
      preferredAccount: new FormControl(null, [Validators.required]),
      timezone: new FormControl('', [Validators.required]),
      username: new FormControl('', [Validators.required]),
    });
  }

  get user(): NewUserForm {
    return this.newUserForm.getRawValue();
  }

  async submitNewUser() {
    // we allowed the User to select ANY account, it may not be in Identity yet.
    // find the Account and convert the preferredAccount ID to an identity entity ID
    const account = this.accountsWithEntities.find(account => account.id === this.user.preferredAccount.id);

    if (account && !account.entityId) {
      try {
        // the entity must not exist yet, create the entity in Identity Service for the Account
        const entity$ = this.desEntityService.createEntity$({ extId: account.id, extSource: 'TOPOLOGY' } as EntityDto);
        const entity: EntityDto = await firstValueFrom(entity$);
        account.entityId = entity.id;
      } catch (err) {
        this.globalAlertService.setError(`There was a problem creating the entity for the Preferred Account.  ${err}`);
      }
    }

    // @ts-expect-error The assign is correct
    const newUser: UserDtoPOST = {
      preferredAccount: account.entityId,
      ...Object.fromEntries(Object.entries(this.user).filter(([key]) => key !== 'preferredAccount')),
    };

    try {
      this.userService
        .createNewUser(newUser)
        .pipe(
          switchMap(user => {
            return forkJoin([this.desEntityService.entities$(), of(user)]);
          }),
        )
        .subscribe(result => {
          const user = result[1];
          this.globalAlertService.setSuccess(
            `${this.user.username} successfully created. Please save the generated password.`,
          );
          if (user?.password) {
            this.generatedPassword = user.password;
          } else {
            this.globalAlertService.setError(
              `An error occurred user password was not returned: ${JSON.stringify(user)}`,
            );
          }
          this.newUserForm.reset();
        });
    } catch (err) {
      this.globalAlertService.setError(`There was a problem creating the user.  ${err}`);
    }
  }

  async goBack(): Promise<void> {
    await this.router.navigate(['/']);
  }
}
